import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Button, Alert, Spinner, Table, Badge, Form } from 'react-bootstrap';
import { sessionStore } from '../stores/SessionStore';
import { userStore } from '../stores/UserStore';
import { academyStore } from '../stores/AcademyStore';
import { batchStore } from '../stores/BatchStore';
const AttendanceDialog = observer(() => {
    const [localAttendance, setLocalAttendance] = useState({});
    const [originalAttendance, setOriginalAttendance] = useState({});
    const [saving, setSaving] = useState(false);
    const session = sessionStore.selectedSession;
    // Initialize local attendance state from session
    React.useEffect(() => {
        if (session) {
            const initial = {};
            const attendance = session.attendance || {};
            // Only those recorded in attendance are considered present (others are absent by default)
            Object.keys(attendance).forEach(userId => {
                if (attendance[userId] && attendance[userId].attended) {
                    initial[userId] = true; // Only record those who are present
                }
            });
            // Collect all participant IDs: direct coaches/players + batch members
            const allParticipantIds = new Set();
            // Add direct coaches and players
            (session.coaches || []).forEach(id => allParticipantIds.add(id));
            (session.players || []).forEach(id => allParticipantIds.add(id));
            // Add coaches and players from batches
            if (session.batches && session.batches.length > 0) {
                session.batches.forEach(batchId => {
                    const batch = batchStore.batches.find(b => b.id === batchId);
                    if (batch) {
                        (batch.coaches || []).forEach(id => allParticipantIds.add(id));
                        (batch.players || []).forEach(id => allParticipantIds.add(id));
                    }
                });
            }
            // Ensure all participants are in the initial state
            Array.from(allParticipantIds).forEach(userId => {
                if (!(userId in initial)) {
                    initial[userId] = false; // Default to absent if not in attendance record
                }
            });
            setLocalAttendance(initial);
            setOriginalAttendance({ ...initial }); // Store original state to detect changes
        }
        else {
            setLocalAttendance({});
            setOriginalAttendance({});
        }
    }, [session]);
    if (!session) {
        return null;
    }
    // Collect all participants: direct coaches/players + batch members
    const allParticipantsMap = new Map();
    // Add direct coaches and players
    (session.coaches || []).forEach(id => {
        allParticipantsMap.set(id, 'coach');
    });
    (session.players || []).forEach(id => {
        allParticipantsMap.set(id, 'player');
    });
    // Add coaches and players from batches
    if (session.batches && session.batches.length > 0) {
        session.batches.forEach(batchId => {
            const batch = batchStore.batches.find(b => b.id === batchId);
            if (batch) {
                (batch.coaches || []).forEach(id => {
                    // Only add if not already in map (direct assignments take precedence)
                    if (!allParticipantsMap.has(id)) {
                        allParticipantsMap.set(id, 'coach');
                    }
                });
                (batch.players || []).forEach(id => {
                    // Only add if not already in map (direct assignments take precedence)
                    if (!allParticipantsMap.has(id)) {
                        allParticipantsMap.set(id, 'player');
                    }
                });
            }
        });
    }
    // Sort participants: coaches first, then players
    const allParticipants = Array.from(allParticipantsMap.entries())
        .map(([id, type]) => ({ id, type }))
        .sort((a, b) => {
        // Coaches come before players
        if (a.type === 'coach' && b.type === 'player')
            return -1;
        if (a.type === 'player' && b.type === 'coach')
            return 1;
        // Within same type, sort by name
        const userA = userStore.users.find(u => u.id === a.id);
        const userB = userStore.users.find(u => u.id === b.id);
        const nameA = userA?.name || a.id;
        const nameB = userB?.name || b.id;
        return nameA.localeCompare(nameB);
    });
    // Group participants by role
    const coaches = allParticipants.filter(p => p.type === 'coach');
    const players = allParticipants.filter(p => p.type === 'player');
    const handleToggleAttendance = (userId) => {
        setLocalAttendance(prev => ({
            ...prev,
            [userId]: !prev[userId]
        }));
    };
    const handleSaveAttendance = async () => {
        if (!academyStore.currentAcademy || !session) {
            return;
        }
        // Get current user ID (assuming first coach can mark attendance)
        // In a real app, this would come from auth context
        // Check direct coaches first, then batch coaches
        let markedBy = undefined;
        if (session.coaches && session.coaches.length > 0) {
            markedBy = session.coaches[0];
        }
        else if (session.batches && session.batches.length > 0) {
            // Find first coach from any batch
            for (const batchId of session.batches) {
                const batch = batchStore.batches.find(b => b.id === batchId);
                if (batch && batch.coaches && batch.coaches.length > 0) {
                    markedBy = batch.coaches[0];
                    break;
                }
            }
        }
        if (!markedBy) {
            return;
        }
        // Find all changed entries (T->F or F->T)
        const changedEntries = [];
        const allUserIds = new Set([
            ...Object.keys(localAttendance),
            ...Object.keys(originalAttendance)
        ]);
        allUserIds.forEach(userId => {
            const currentValue = localAttendance[userId] || false;
            const originalValue = originalAttendance[userId] || false;
            // Only add if value has changed
            if (currentValue !== originalValue) {
                changedEntries.push({
                    userId,
                    present: currentValue
                });
            }
        });
        // If no changes, just close
        if (changedEntries.length === 0) {
            sessionStore.closeAttendanceDialog();
            return;
        }
        setSaving(true);
        try {
            // Send all changes in a single bulk update call
            await sessionStore.updateAttendanceBulk(session.id, changedEntries, markedBy);
            // Refresh session data
            if (academyStore.currentAcademy) {
                await sessionStore.fetchSessions(academyStore.currentAcademy.id);
            }
            sessionStore.closeAttendanceDialog();
        }
        catch (error) {
            // Error handled by store
        }
        finally {
            setSaving(false);
        }
    };
    const handleClose = () => {
        sessionStore.closeAttendanceDialog();
    };
    return (_jsxs(Modal, { show: sessionStore.isAttendanceDialogOpen, onHide: handleClose, size: "lg", children: [_jsx(Modal.Header, { closeButton: true, children: _jsxs(Modal.Title, { children: ["Mark Attendance - ", session.title] }) }), _jsxs(Modal.Body, { children: [sessionStore.hasError && (_jsx(Alert, { variant: "danger", className: "mb-3", children: sessionStore.error })), _jsxs("p", { className: "text-muted", children: ["Scheduled: ", new Date(session.schedule).toLocaleString()] }), _jsxs(Table, { striped: true, hover: true, children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Name" }), _jsx("th", { children: "Role" }), _jsx("th", { children: "Attendance" })] }) }), _jsxs("tbody", { children: [coaches.length > 0 && (_jsx(_Fragment, { children: coaches.map(({ id, type }) => {
                                            const user = userStore.users.find(u => u.id === id);
                                            const present = localAttendance[id] || false;
                                            const existingRecord = session.attendance && session.attendance[id] ? session.attendance[id] : undefined;
                                            return user ? (_jsxs("tr", { children: [_jsx("td", { children: user.name }), _jsx("td", { children: _jsx(Badge, { bg: "info", children: "coach" }) }), _jsxs("td", { children: [_jsx(Form.Check, { type: "checkbox", checked: present, onChange: () => handleToggleAttendance(id), label: "Present" }), existingRecord && (_jsxs("small", { className: "text-muted d-block", children: ["Marked by ", userStore.users.find(u => u.id === existingRecord.markedBy)?.name || 'Unknown', " at ", new Date(existingRecord.markedAt).toLocaleString()] }))] })] }, id)) : null;
                                        }) })), players.length > 0 && (_jsx(_Fragment, { children: players.map(({ id, type }) => {
                                            const user = userStore.users.find(u => u.id === id);
                                            const present = localAttendance[id] || false;
                                            const existingRecord = session.attendance && session.attendance[id] ? session.attendance[id] : undefined;
                                            return user ? (_jsxs("tr", { children: [_jsx("td", { children: user.name }), _jsx("td", { children: _jsx(Badge, { bg: "success", children: "player" }) }), _jsxs("td", { children: [_jsx(Form.Check, { type: "checkbox", checked: present, onChange: () => handleToggleAttendance(id), label: "Present" }), existingRecord && (_jsxs("small", { className: "text-muted d-block", children: ["Marked by ", userStore.users.find(u => u.id === existingRecord.markedBy)?.name || 'Unknown', " at ", new Date(existingRecord.markedAt).toLocaleString()] }))] })] }, id)) : null;
                                        }) }))] })] }), coaches.length === 0 && players.length === 0 && (_jsx("p", { className: "text-muted", children: "No participants assigned to this session" }))] }), _jsxs(Modal.Footer, { children: [_jsx(Button, { variant: "secondary", onClick: handleClose, disabled: saving, className: "btn-touch w-mobile-100", children: "Close" }), _jsxs(Button, { variant: "primary", onClick: handleSaveAttendance, disabled: saving || (coaches.length === 0 && players.length === 0), className: "btn-touch w-mobile-100", children: [saving && (_jsx(Spinner, { animation: "border", size: "sm", className: "me-2" })), "Save Attendance"] })] })] }));
});
export default AttendanceDialog;
